import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Plus, Edit2, Trash2, Users, FileText, Settings,
  CheckCircle, XCircle, Eye, Loader2, Search, AlertTriangle,
  Globe, Mail, Calendar, BarChart3, Crown, KeyRound, UserCog,
  Power, PowerOff, Copy, Check, MoreVertical, Clock, RefreshCw, RotateCcw, ShieldAlert, Download, Palette
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LayoutStyleSection } from '@/pages/admin/settings/LayoutStyleSection';
import api from '@/lib/api';
import { UserEditDialog } from './tenants/UserEditDialog';
import { ResetPasswordDialog } from './tenants/ResetPasswordDialog';
import { DeleteTenantDialog } from './tenants/DeleteTenantDialog';
import { ResetDemoDialog } from './tenants/ResetDemoDialog';
import { CreateTenantDialog } from './tenants/CreateTenantDialog';
import { EditTenantDialog } from './tenants/EditTenantDialog';
import { BrandColorDialog } from './tenants/BrandColorDialog';
import { TriageModeDialog } from './tenants/TriageModeDialog';
import { CreateUserDialog } from './tenants/CreateUserDialog';
import { UserDeleteDialog } from './tenants/UserDeleteDialog';

// Fallback used only when the /super-admin/plans endpoint is unreachable.
// The live catalogue (fetched at mount) always takes precedence — it is the
// same catalogue that the SaaS-Hub syncs via /api/provisioning/plans and that
// the operator edits in /super-admin/planos, so any change there flows here.
const FALLBACK_PLANS = [
  { key: 'starter', name: 'Starter', max_users: 5, max_reports_month: 50 },
  { key: 'professional', name: 'Professional', max_users: 20, max_reports_month: 200 },
  { key: 'enterprise', name: 'Enterprise', max_users: 100, max_reports_month: 1000 }
];

const INVITE_EXPIRY_OPTIONS = [
  { value: 24, label: '24 horas' },
  { value: 72, label: '3 dias' },
  { value: 168, label: '7 dias' }
];

const ROLES = [
  { value: 'company_admin', label: 'Administrador' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'triage_manager', label: 'Gestor de Triagem' },
  { value: 'case_manager', label: 'Gestor de Casos' },
  { value: 'investigator', label: 'Investigador' },
  { value: 'dpo', label: 'DPO' }
];

const TenantsManagementPage = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    domain: '',
    plan: 'starter',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    triage_mode: 'cogniseg',
    triage_name: '',
    triage_email: '',
    triage_password: ''
  });

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'case_manager',
    send_invite: true,
    invite_expiry_hours: 168
  });

  // Tenant-user management (edit / reset password / activate)
  const [showUserEditDialog, setShowUserEditDialog] = useState(false);
  const [showResetPwDialog, setShowResetPwDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ name: '', email: '', role: 'case_manager', is_active: true });
  const [editUserError, setEditUserError] = useState('');
  const [editUserSuccess, setEditUserSuccess] = useState('');
  const [resetPwForm, setResetPwForm] = useState({ mode: 'generate', password: '' });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copiedPw, setCopiedPw] = useState(false);
  const [userBusy, setUserBusy] = useState('');

  // Delete-tenant-user (super admin only, activity gated)
  const [showUserDeleteDialog, setShowUserDeleteDialog] = useState(false);
  const [userDeleteTarget, setUserDeleteTarget] = useState(null);
  const [userDeleteActivity, setUserDeleteActivity] = useState(null);
  const [userDeleteError, setUserDeleteError] = useState('');
  const [userDeleteLoading, setUserDeleteLoading] = useState(false);

  // Tenant deletion / RTBF / demo reset
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteMode, setDeleteMode] = useState('hard'); // 'hard' | 'rtbf'
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState('');
  const [rtbfYears, setRtbfYears] = useState(5);
  const [deleting, setDeleting] = useState(false);
  const [showResetDemoDialog, setShowResetDemoDialog] = useState(false);
  const [resetDemoTarget, setResetDemoTarget] = useState(null);
  const [exporting, setExporting] = useState('');

  // Triage manager mode (CogniSeg <-> Client) for an EXISTING tenant
  const [showTriageDialog, setShowTriageDialog] = useState(false);
  const [triageForm, setTriageForm] = useState({ triage_mode: 'cogniseg', triage_name: '', triage_email: '', triage_password: '' });
  const [triageSaving, setTriageSaving] = useState(false);
  const [triageError, setTriageError] = useState('');

  // Brand colour detection/edit for an EXISTING tenant
  const [showColorDialog, setShowColorDialog] = useState(false);
  const [colorForm, setColorForm] = useState({ website_url: '', primary_color: '#0F172A' });
  const [colorSaving, setColorSaving] = useState(false);
  const [colorDetecting, setColorDetecting] = useState(false);
  const [colorError, setColorError] = useState('');

  // Landing branding editor (super-admin view of tenant Definições > Layout)
  const [landingForm, setLandingForm] = useState({
    layout_style: 'classic',
    institutional_text: '',
    support_email: '',
    support_signature: '',
    primary_color: '#0F172A',
  });
  const [landingSaving, setLandingSaving] = useState(false);
  const [landingError, setLandingError] = useState('');
  const [landingSuccess, setLandingSuccess] = useState('');

  // Global email toggle (super-admin bypass)
  const [globalEmailSaving, setGlobalEmailSaving] = useState(false);

  // Live plans catalogue (synced with the SaaS-Hub via /provisioning/plans).
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  useEffect(() => {
    let cancelled = false;
    api.get('/super-admin/plans')
      .then(res => {
        if (cancelled) return;
        const live = res.data?.plans;
        if (Array.isArray(live) && live.length) setPlans(live);
      })
      .catch(() => { /* stay on fallback */ });
    return () => { cancelled = true; };
  }, []);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/super-admin/tenants');
      setTenants(response.data.tenants || []);
    } catch (err) {
      setError('Erro ao carregar tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleCreateTenant = async () => {
    setSaving(true);
    setError('');
    
    try {
      await api.post('/super-admin/tenants', formData);
      setSuccess('Cliente criado com sucesso');
      setShowCreateDialog(false);
      resetForm();
      fetchTenants();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    setError('');
    
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}`, {
        name: formData.name,
        domain: formData.domain,
        plan: formData.plan,
        is_active: formData.is_active,
        is_demo: !!formData.is_demo,
        max_users: plans.find(p => p.key === formData.plan)?.max_users ?? 5,
        max_reports_month: plans.find(p => p.key === formData.plan)?.max_reports_month ?? 50
      });
      setSuccess('Cliente atualizado');
      setShowEditDialog(false);
      fetchTenants();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (tenant) => {
    try {
      await api.patch(`/super-admin/tenants/${tenant.id}`, {
        is_active: !tenant.is_active
      });
      fetchTenants();
    } catch (err) {
      setError('Erro ao alterar estado');
    }
  };

  const openDeleteDialog = (tenant, mode) => {
    setDeleteTarget(tenant);
    setDeleteMode(mode);
    setConfirmSlug('');
    setRtbfYears(5);
    setShowDeleteDialog(true);
  };

  const openResetDemoDialog = (tenant) => {
    setResetDemoTarget(tenant);
    setShowResetDemoDialog(true);
  };

  const openTriageDialog = () => {
    const currentMode = tenantDetail?.tenant?.triage_mode || 'cogniseg';
    const localManager = tenantDetail?.users?.find(u => u.role === 'triage_manager' && !u.cogniseg_managed);
    setTriageForm({
      triage_mode: currentMode,
      triage_name: localManager?.name || '',
      triage_email: localManager?.email || '',
      triage_password: ''
    });
    setTriageError('');
    setShowTriageDialog(true);
  };

  const handleUpdateTriageMode = async () => {
    if (!selectedTenant) return;
    setTriageSaving(true);
    setTriageError('');
    try {
      const res = await api.post(`/super-admin/tenants/${selectedTenant.id}/triage-mode`, triageForm);
      setSuccess(res.data.message || 'Modo de Gestor de Triagem atualizado');
      setShowTriageDialog(false);
      handleViewDetails(selectedTenant);
      fetchTenants();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setTriageError(err.response?.data?.detail || 'Erro ao atualizar o modo de Gestor de Triagem');
    } finally {
      setTriageSaving(false);
    }
  };

  const openColorDialog = () => {
    setColorForm({
      website_url: tenantDetail?.settings?.website_url || '',
      primary_color: tenantDetail?.settings?.primary_color || '#0F172A'
    });
    setColorError('');
    setShowColorDialog(true);
  };

  const handleDetectColor = async () => {
    if (!colorForm.website_url) {
      setColorError('Indique o URL do site primeiro');
      return;
    }
    setColorDetecting(true);
    setColorError('');
    try {
      const res = await api.post('/admin/settings/detect-brand-color', { url: colorForm.website_url });
      setColorForm(prev => ({ ...prev, primary_color: res.data.color }));
    } catch (err) {
      setColorError(err.response?.data?.detail || 'Não foi possível detetar uma cor no site indicado');
    } finally {
      setColorDetecting(false);
    }
  };

  const handleSaveColor = async () => {
    if (!selectedTenant) return;
    setColorSaving(true);
    setColorError('');
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}/settings`, colorForm);
      setSuccess('Cor da marca atualizada');
      setShowColorDialog(false);
      handleViewDetails(selectedTenant);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setColorError(err.response?.data?.detail || 'Erro ao guardar a cor da marca');
    } finally {
      setColorSaving(false);
    }
  };

  const handleExportTenant = async (tenant) => {
    setExporting(tenant.id);
    setError('');
    try {
      const res = await api.get(`/super-admin/tenants/${tenant.id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${tenant.slug}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Exportação concluída — ficheiro descarregado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao exportar dados do cliente');
    } finally {
      setExporting('');
    }
  };

  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await api.post(`/super-admin/tenants/${deleteTarget.id}/hard-delete`, { confirm_slug: confirmSlug });
      setSuccess(`Cliente "${deleteTarget.name}" e todos os dados foram eliminados definitivamente`);
      setShowDeleteDialog(false);
      fetchTenants();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao eliminar cliente');
    } finally {
      setDeleting(false);
    }
  };

  const handleScheduleDeletion = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      const res = await api.post(`/super-admin/tenants/${deleteTarget.id}/schedule-deletion`, { retention_years: rtbfYears });
      setSuccess(res.data.message);
      setShowDeleteDialog(false);
      fetchTenants();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao agendar eliminação');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDeletion = async (tenant) => {
    setError('');
    try {
      await api.post(`/super-admin/tenants/${tenant.id}/cancel-deletion`);
      setSuccess('Agendamento de eliminação cancelado. Cliente reativado.');
      fetchTenants();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cancelar eliminação');
    }
  };

  const handleResetDemo = async () => {
    if (!resetDemoTarget) return;
    setDeleting(true);
    setError('');
    try {
      const res = await api.post(`/super-admin/tenants/${resetDemoTarget.id}/reset-demo-data`);
      setSuccess(res.data.message);
      setShowResetDemoDialog(false);
      fetchTenants();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao repor dados do demo');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewDetails = async (tenant) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenant.id}`);
      setTenantDetail(response.data);
      setSelectedTenant(tenant);
      // Seed landing form from tenant settings (fallback to sensible defaults)
      const s = response.data?.settings || {};
      setLandingForm({
        layout_style: s.layout_style || 'classic',
        institutional_text: s.institutional_text || '',
        support_email: s.support_email || '',
        support_signature: s.support_signature || '',
        primary_color: s.primary_color || '#0F172A',
      });
      setLandingError('');
      setLandingSuccess('');
      setShowDetailDialog(true);
    } catch (err) {
      setError('Erro ao carregar detalhes');
    }
  };

  const handleSaveLandingBranding = async () => {
    if (!selectedTenant) return;
    setLandingSaving(true);
    setLandingError('');
    setLandingSuccess('');
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}/branding`, landingForm);
      // Refresh tenant detail first (this resets landingSuccess), THEN set the
      // success flag so the alert actually renders after the refetch.
      await handleViewDetails(selectedTenant);
      setLandingSuccess('Landing pública atualizada com sucesso');
      setTimeout(() => setLandingSuccess(''), 3500);
    } catch (err) {
      setLandingError(err.response?.data?.detail || 'Erro ao guardar landing');
    } finally {
      setLandingSaving(false);
    }
  };

  const handleToggleGlobalEmail = async () => {
    if (!selectedTenant || !tenantDetail) return;
    const currentValue = tenantDetail.settings?.use_global_email !== false;
    setGlobalEmailSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}/email-config`, {
        use_global_email: !currentValue,
      });
      await handleViewDetails(selectedTenant);
      setSuccess(currentValue ? 'Email global desativado' : 'Email global ativado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao alternar email global');
    } finally {
      setGlobalEmailSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    setError('');
    
    try {
      const res = await api.post(`/super-admin/tenants/${selectedTenant.id}/users`, userForm);
      setSuccess(res.data.invite_sent ? 'Utilizador criado e convite enviado por email' : 'Utilizador criado');
      setShowUserDialog(false);
      setUserForm({ name: '', email: '', password: '', role: 'case_manager', send_invite: true, invite_expiry_hours: 168 });
      handleViewDetails(selectedTenant);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar utilizador');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async (user, expiryHours = 168) => {
    if (!selectedTenant) return;
    setUserBusy(user.id);
    setError('');
    try {
      await api.post(`/super-admin/tenants/${selectedTenant.id}/users/${user.id}/send-invite`, { expiry_hours: expiryHours });
      setSuccess(`Convite enviado para ${user.email} (válido ${INVITE_EXPIRY_OPTIONS.find(o => o.value === expiryHours)?.label || '7 dias'})`);
      handleViewDetails(selectedTenant);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setUserBusy('');
    }
  };

  const openUserEditDialog = (user) => {
    setSelectedUser(user);
    setUserEditForm({ name: user.name || '', email: user.email || '', role: user.role, is_active: user.is_active !== false });
    setEditUserError('');
    setEditUserSuccess('');
    setShowUserEditDialog(true);
  };

  const openResetPwDialog = (user) => {
    setSelectedUser(user);
    setResetPwForm({ mode: 'generate', password: '' });
    setGeneratedPassword('');
    setShowResetPwDialog(true);
  };

  const handleEditUser = async () => {
    if (!selectedTenant || !selectedUser) return;
    setSaving(true);
    setEditUserError('');
    setEditUserSuccess('');
    setError('');
    // Only send fields that actually changed. This avoids re-sending an
    // unchanged `role` for legacy users where the value doesn't match the
    // current enum (would return 400 "Role inválido" silently behind the modal).
    const payload = {};
    if ((userEditForm.name || '').trim() !== (selectedUser.name || '').trim()) {
      payload.name = userEditForm.name.trim();
    }
    if ((userEditForm.email || '').trim().toLowerCase() !== (selectedUser.email || '').trim().toLowerCase()) {
      payload.email = userEditForm.email.trim().toLowerCase();
    }
    if (userEditForm.role && userEditForm.role !== selectedUser.role) {
      payload.role = userEditForm.role;
    }
    if (userEditForm.is_active !== (selectedUser.is_active !== false)) {
      payload.is_active = userEditForm.is_active;
    }
    if (Object.keys(payload).length === 0) {
      setEditUserError('Nenhum campo foi alterado.');
      setSaving(false);
      return;
    }
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}/users/${selectedUser.id}`, payload);
      setEditUserSuccess('Alterações guardadas');
      setSuccess('Utilizador atualizado');
      // Close after a short delay so the user actually sees the confirmation
      setTimeout(() => {
        setShowUserEditDialog(false);
        setEditUserSuccess('');
        handleViewDetails(selectedTenant);
      }, 900);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(d => d.msg || String(d)).join('; ') : null)
          || err.message
          || 'Erro ao atualizar utilizador';
      setEditUserError(msg);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserActive = async (user) => {
    if (!selectedTenant) return;
    setUserBusy(user.id);
    setError('');
    try {
      await api.patch(`/super-admin/tenants/${selectedTenant.id}/users/${user.id}`, {
        is_active: user.is_active === false
      });
      handleViewDetails(selectedTenant);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao alterar estado do utilizador');
    } finally {
      setUserBusy('');
    }
  };

  // ---- Delete tenant user (super admin, activity-gated) --------------------
  const openUserDeleteDialog = async (user) => {
    if (!selectedTenant) return;
    setUserDeleteTarget(user);
    setUserDeleteActivity(null);
    setUserDeleteError('');
    setShowUserDeleteDialog(true);
    setUserDeleteLoading(true);
    try {
      const res = await api.get(`/super-admin/tenants/${selectedTenant.id}/users/${user.id}/activity`);
      setUserDeleteActivity(res.data);
    } catch (err) {
      setUserDeleteError(err.response?.data?.detail || 'Não foi possível verificar a atividade do utilizador.');
    } finally {
      setUserDeleteLoading(false);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedTenant || !userDeleteTarget) return;
    setUserDeleteLoading(true);
    setUserDeleteError('');
    try {
      await api.delete(`/super-admin/tenants/${selectedTenant.id}/users/${userDeleteTarget.id}`);
      setShowUserDeleteDialog(false);
      setUserDeleteTarget(null);
      setSuccess(`Utilizador ${userDeleteTarget.email} eliminado`);
      setTimeout(() => setSuccess(''), 4000);
      handleViewDetails(selectedTenant);
    } catch (err) {
      setUserDeleteError(err.response?.data?.detail || 'Erro ao eliminar utilizador');
    } finally {
      setUserDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedTenant || !selectedUser) return;
    setSaving(true);
    setError('');
    setGeneratedPassword('');
    try {
      const payload = resetPwForm.mode === 'generate'
        ? { generate: true }
        : { password: resetPwForm.password };
      const res = await api.post(
        `/super-admin/tenants/${selectedTenant.id}/users/${selectedUser.id}/reset-password`,
        payload
      );
      if (res.data.password) {
        setGeneratedPassword(res.data.password);
      } else {
        setShowResetPwDialog(false);
        setSuccess(res.data.email_sent ? 'Password reposta e enviada por email' : 'Password reposta (email não enviado)');
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao repor password');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      domain: tenant.domain || '',
      plan: tenant.plan || 'starter',
      is_active: tenant.is_active,
      is_demo: !!tenant.is_demo
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      domain: '',
      plan: 'starter',
      admin_name: '',
      admin_email: '',
      admin_password: '',
      triage_mode: 'cogniseg',
      triage_name: '',
      triage_email: '',
      triage_password: ''
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-indigo-700" />
            Gestão de Clientes (Tenants)
          </h1>
          <p className="text-slate-500 mt-1">
            Gerir instâncias de clientes na plataforma
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)} 
          className="bg-indigo-700 hover:bg-amber-600 text-black"
          data-testid="create-tenant-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-500/50">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-900/50 border-green-500/50">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <AlertDescription className="text-green-600">{success}</AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Pesquisar por nome ou slug..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
          data-testid="search-tenants"
        />
      </div>

      {/* Tenants Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredTenants.map((tenant) => (
          <Card key={tenant.id} className={`bg-white border-slate-200 ${!tenant.is_active ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                    {tenant.name}
                    {tenant.slug === 'demo' && (
                      <Crown className="w-4 h-4 text-indigo-700" title="Instância Demo" />
                    )}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1 mt-1 text-slate-500">
                    <Globe className="w-3 h-3" />
                    <a
                      href={`/denunciar/${tenant.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-indigo-700 hover:underline transition-colors"
                      title={`Abrir portal público de ${tenant.name} em nova janela`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`tenant-public-portal-link-${tenant.id}`}
                    >
                      /{tenant.slug}
                    </a>
                  </CardDescription>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant={tenant.is_active ? "default" : "secondary"} className={tenant.is_active ? "bg-green-100 text-green-700 border-green-500/30" : "bg-slate-200 text-slate-500"}>
                    {tenant.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                  {tenant.pending_deletion && (
                    <Badge className="bg-red-100 text-red-700 border border-red-200" data-testid={`tenant-pending-deletion-${tenant.id}`} title={`Eliminação agendada para ${tenant.deletion_scheduled_at ? new Date(tenant.deletion_scheduled_at).toLocaleDateString('pt-PT') : ''}`}>
                      <Clock className="w-3 h-3 mr-1" /> Elim. {tenant.deletion_scheduled_at ? new Date(tenant.deletion_scheduled_at).toLocaleDateString('pt-PT') : ''}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Plano</span>
                  <Badge variant="outline" className="border-indigo-200 text-indigo-700">{tenant.plan || 'starter'}</Badge>
                </div>
                
                {tenant.stats && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Utilizadores
                      </span>
                      <span className="font-medium text-slate-900">{tenant.stats.users_count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Denúncias
                      </span>
                      <span className="font-medium text-slate-900">{tenant.stats.reports_count}</span>
                    </div>
                    {tenant.stats.reports_pending > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-indigo-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Pendentes
                        </span>
                        <span className="font-medium text-indigo-700">{tenant.stats.reports_pending}</span>
                      </div>
                    )}
                  </>
                )}

                <Separator className="bg-slate-700" />

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-700"
                    onClick={() => handleViewDetails(tenant)}
                    data-testid={`view-tenant-${tenant.id}`}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Ver
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-700"
                    onClick={() => openEditDialog(tenant)}
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    variant={tenant.is_active ? "destructive" : "default"}
                    size="sm"
                    onClick={() => handleToggleActive(tenant)}
                    disabled={tenant.slug === 'demo'}
                    className={tenant.is_active ? "bg-red-100 text-red-700 hover:bg-red-500/30" : "bg-green-100 text-green-700 hover:bg-green-500/30"}
                  >
                    {tenant.is_active ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid={`tenant-danger-menu-${tenant.id}`} title="Mais ações">
                        <MoreVertical className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => handleExportTenant(tenant)} disabled={exporting === tenant.id} data-testid={`export-tenant-${tenant.id}`}>
                        {exporting === tenant.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2 text-slate-600" />} Exportar dados (ZIP)
                      </DropdownMenuItem>
                      {(tenant.slug === 'demo' || tenant.is_demo) ? (
                        <>
                          <DropdownMenuLabel>{tenant.slug === 'demo' ? 'Instância Demo' : 'Instância de teste'}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openResetDemoDialog(tenant)} data-testid={`reset-demo-${tenant.id}`}>
                            <RefreshCw className="w-4 h-4 mr-2 text-indigo-700" /> Repor dados de teste
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <>
                          <DropdownMenuLabel>Zona de perigo</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {tenant.pending_deletion ? (
                            <DropdownMenuItem onClick={() => handleCancelDeletion(tenant)} data-testid={`cancel-deletion-${tenant.id}`}>
                              <RotateCcw className="w-4 h-4 mr-2 text-emerald-700" /> Cancelar eliminação agendada
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => openDeleteDialog(tenant, 'rtbf')} data-testid={`rtbf-${tenant.id}`}>
                              <Clock className="w-4 h-4 mr-2 text-amber-600" /> Retenção legal (RTBF)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openDeleteDialog(tenant, 'hard')} className="text-red-600 focus:text-red-700" data-testid={`hard-delete-${tenant.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar definitivamente
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTenants.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">Nenhum cliente encontrado</p>
        </div>
      )}

      {/* Create Tenant Dialog */}
      <CreateTenantDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        formData={formData}
        setFormData={setFormData}
        plans={plans}
        saving={saving}
        onSubmit={handleCreateTenant}
        generateSlug={generateSlug}
      />

      {/* Edit Tenant Dialog */}
      <EditTenantDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        formData={formData}
        setFormData={setFormData}
        plans={plans}
        saving={saving}
        onSubmit={handleUpdateTenant}
      />

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-50 border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Building2 className="w-5 h-5 text-indigo-700" />
              {tenantDetail?.tenant?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              /{tenantDetail?.tenant?.slug}
            </DialogDescription>
          </DialogHeader>
          
          {tenantDetail && (
            <Tabs defaultValue="users">
              <TabsList className="grid w-full grid-cols-4 bg-white">
                <TabsTrigger value="users" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Utilizadores</TabsTrigger>
                <TabsTrigger value="stats" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Estatísticas</TabsTrigger>
                <TabsTrigger value="settings" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Configurações</TabsTrigger>
                <TabsTrigger value="landing" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="super-admin-landing-tab">Landing</TabsTrigger>
              </TabsList>

              <TabsContent value="users" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-slate-900">Utilizadores ({tenantDetail.users?.length || 0})</h4>
                  <Button size="sm" onClick={() => setShowUserDialog(true)} className="bg-indigo-700 hover:bg-amber-600 text-black">
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {tenantDetail.users?.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg" data-testid={`tenant-user-row-${user.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{user.name}</p>
                        <p className="text-sm text-slate-500 truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="border-indigo-200 text-indigo-700 hidden sm:inline-flex">{ROLES.find(r => r.value === user.role)?.label || user.role}</Badge>
                        {user.invite_pending ? (
                          <Badge className="bg-amber-100 text-amber-700 border border-amber-200" data-testid={`user-status-${user.id}`}>Convite pendente</Badge>
                        ) : (
                          <Badge variant={user.is_active ? "default" : "secondary"} className={user.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"} data-testid={`user-status-${user.id}`}>
                            {user.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        )}
                        {user.role !== 'super_admin' && (
                          <>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" disabled={userBusy === user.id} className="h-8 w-8 text-slate-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Enviar convite / link de acesso" data-testid={`invite-user-${user.id}`}>
                                  {userBusy === user.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Enviar link válido por</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {INVITE_EXPIRY_OPTIONS.map(opt => (
                                  <DropdownMenuItem key={opt.value} onClick={() => handleSendInvite(user, opt.value)} data-testid={`invite-user-${user.id}-${opt.value}`}>
                                    {opt.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50"
                              onClick={() => openUserEditDialog(user)} title="Editar utilizador" data-testid={`edit-user-${user.id}`}>
                              <UserCog className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => openResetPwDialog(user)} title="Repor password" data-testid={`reset-pw-${user.id}`}>
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" disabled={userBusy === user.id}
                              className={`h-8 w-8 ${user.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                              onClick={() => handleToggleUserActive(user)}
                              title={user.is_active ? 'Desativar' : 'Ativar'} data-testid={`toggle-user-${user.id}`}>
                              {userBusy === user.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (user.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />)}
                            </Button>
                            {user.role !== 'company_admin' && !user.is_cogniseg_global && !user.cogniseg_managed && (
                              <Button size="icon" variant="ghost"
                                className="h-8 w-8 text-slate-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openUserDeleteDialog(user)}
                                title="Eliminar utilizador (só sem atividade)"
                                data-testid={`delete-user-${user.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {!tenantDetail.users?.length && (
                    <p className="text-center text-slate-500 py-4">Nenhum utilizador</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="stats" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-600">Total Denúncias</p>
                    <p className="text-2xl font-bold text-slate-900">{tenantDetail.stats?.reports_total || 0}</p>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <p className="text-sm text-indigo-700">Pendentes</p>
                    <p className="text-2xl font-bold text-slate-900">{tenantDetail.stats?.reports_pending || 0}</p>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-sm text-green-600">Concluídas</p>
                    <p className="text-2xl font-bold text-slate-900">{tenantDetail.stats?.reports_closed || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-300">
                    <p className="text-sm text-slate-500">Este Mês</p>
                    <p className="text-2xl font-bold text-slate-900">{tenantDetail.stats?.reports_this_month || 0}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="settings" className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-white rounded-lg">
                    <span className="text-slate-500">Plano</span>
                    <Badge className="bg-amber-100 text-amber-700">{tenantDetail.tenant?.plan}</Badge>
                  </div>
                  <div className="flex justify-between p-3 bg-white rounded-lg">
                    <span className="text-slate-500">Máx. Utilizadores</span>
                    <span className="text-slate-900">{tenantDetail.tenant?.max_users}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white rounded-lg">
                    <span className="text-slate-500">Máx. Denúncias/Mês</span>
                    <span className="text-slate-900">{tenantDetail.tenant?.max_reports_month}</span>
                  </div>
                  <Separator className="bg-slate-700" />
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="text-slate-500 block">Usa Email Global</span>
                      <Badge variant={tenantDetail.settings?.use_global_email !== false ? "default" : "secondary"} className={`mt-1 ${tenantDetail.settings?.use_global_email !== false ? "bg-green-100 text-green-700" : "bg-slate-600"}`}>
                        {tenantDetail.settings?.use_global_email !== false ? 'Sim' : 'Não'}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleToggleGlobalEmail}
                      disabled={globalEmailSaving}
                      className="border-slate-300 text-slate-600 hover:bg-slate-100"
                      data-testid="toggle-global-email-btn"
                    >
                      {globalEmailSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                      {tenantDetail.settings?.use_global_email !== false ? 'Desativar email global' : 'Ativar email global'}
                    </Button>
                  </div>
                  <Separator className="bg-slate-700" />
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="text-slate-500 block">Cor da Marca</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: tenantDetail?.settings?.primary_color || '#0F172A' }} />
                        <span className="text-slate-900 text-sm">{tenantDetail?.settings?.primary_color || '#0F172A'}</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-100"
                      onClick={openColorDialog} data-testid="change-brand-color-btn">
                      <Palette className="w-3 h-3 mr-1" /> Detetar / Alterar
                    </Button>
                  </div>
                  <Separator className="bg-slate-700" />
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="text-slate-500 block">Gestor de Triagem</span>
                      <Badge className={tenantDetail.tenant?.triage_mode === 'cogniseg' ? 'bg-indigo-100 text-indigo-700 mt-1' : 'bg-slate-200 text-slate-700 mt-1'}>
                        {tenantDetail.tenant?.triage_mode === 'cogniseg' ? 'Serviço CogniSeg' : 'Gestor do próprio cliente'}
                      </Badge>
                    </div>
                    <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-100"
                      onClick={openTriageDialog} data-testid="change-triage-mode-btn">
                      <UserCog className="w-3 h-3 mr-1" /> Alterar
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="landing" className="space-y-4" data-testid="super-admin-landing-tabcontent">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-medium text-slate-900">Landing Pública do Canal</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Escolha o template da landing, texto institucional, email e assinatura de suporte
                      exibidos em <span className="font-mono">/{tenantDetail?.tenant?.slug}</span>.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSaveLandingBranding}
                    disabled={landingSaving}
                    className="bg-indigo-700 hover:bg-amber-600 text-white shrink-0"
                    data-testid="super-admin-save-landing-btn"
                  >
                    {landingSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                    Guardar
                  </Button>
                </div>

                {landingError && (
                  <Alert variant="destructive" data-testid="super-admin-landing-error">
                    <AlertDescription>{landingError}</AlertDescription>
                  </Alert>
                )}
                {landingSuccess && (
                  <Alert className="bg-emerald-50 border-emerald-200 text-emerald-900" data-testid="super-admin-landing-success">
                    <AlertDescription>{landingSuccess}</AlertDescription>
                  </Alert>
                )}

                <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                  <LayoutStyleSection
                    brandingData={landingForm}
                    setBrandingData={setLandingForm}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Brand Colour Dialog (detect from client's website or set manually) */}
      <BrandColorDialog
        open={showColorDialog}
        onOpenChange={setShowColorDialog}
        tenantName={selectedTenant?.name}
        form={colorForm}
        setForm={setColorForm}
        error={colorError}
        saving={colorSaving}
        detecting={colorDetecting}
        onDetect={handleDetectColor}
        onSubmit={handleSaveColor}
      />

      {/* Triage Mode Dialog (change/activate CogniSeg or Client triage manager on an EXISTING tenant) */}
      <TriageModeDialog
        open={showTriageDialog}
        onOpenChange={setShowTriageDialog}
        tenantName={selectedTenant?.name}
        form={triageForm}
        setForm={setTriageForm}
        error={triageError}
        saving={triageSaving}
        currentModeIsClient={tenantDetail?.tenant?.triage_mode === 'client'}
        onSubmit={handleUpdateTriageMode}
      />

      {/* Create User Dialog */}
      <CreateUserDialog
        open={showUserDialog}
        onOpenChange={setShowUserDialog}
        tenantName={selectedTenant?.name}
        form={userForm}
        setForm={setUserForm}
        saving={saving}
        onSubmit={handleCreateUser}
        inviteExpiryOptions={INVITE_EXPIRY_OPTIONS}
        roles={ROLES}
      />

      {/* Delete user (super admin, activity-gated) */}
      <UserDeleteDialog
        open={showUserDeleteDialog}
        onOpenChange={setShowUserDeleteDialog}
        targetUser={userDeleteTarget}
        activity={userDeleteActivity}
        error={userDeleteError}
        loading={userDeleteLoading}
        onConfirm={handleConfirmDeleteUser}
      />

      {/* Edit User Dialog */}
      <UserEditDialog
        open={showUserEditDialog}
        onOpenChange={setShowUserEditDialog}
        selectedUser={selectedUser}
        form={userEditForm}
        setForm={setUserEditForm}
        onSave={handleEditUser}
        saving={saving}
        error={editUserError}
        success={editUserSuccess}
        roles={ROLES}
      />

      {/* Reset Password Dialog */}
      <ResetPasswordDialog
        open={showResetPwDialog}
        onOpenChange={setShowResetPwDialog}
        selectedUser={selectedUser}
        form={resetPwForm}
        setForm={setResetPwForm}
        onReset={handleResetPassword}
        saving={saving}
        generatedPassword={generatedPassword}
        setGeneratedPassword={setGeneratedPassword}
        copied={copiedPw}
        setCopied={setCopiedPw}
      />

      {/* Delete / RTBF Dialog */}
      <DeleteTenantDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        mode={deleteMode}
        target={deleteTarget}
        confirmSlug={confirmSlug}
        setConfirmSlug={setConfirmSlug}
        rtbfYears={rtbfYears}
        setRtbfYears={setRtbfYears}
        onHardDelete={handleHardDelete}
        onScheduleRtbf={handleScheduleDeletion}
        onExportBeforeDelete={handleExportTenant}
        exporting={exporting}
        deleting={deleting}
      />

      {/* Reset Demo Data Dialog */}
      <ResetDemoDialog
        open={showResetDemoDialog}
        onOpenChange={setShowResetDemoDialog}
        onConfirm={handleResetDemo}
        deleting={deleting}
        tenant={resetDemoTarget}
      />
    </div>
  );
};

export default TenantsManagementPage;
